import { Platform } from 'react-native';
import { getLocales } from 'expo-localization';

/**
 * Какую редакцию пользовательского соглашения показывать (CORE-1555).
 *
 * Для российского контура оператор — российское юрлицо; соглашение с
 * иностранным оператором (AGAPE.IO Limited) остаётся только для тех, у кого
 * И локация не РФ, И нет русского языка. Любое сомнение решается в пользу
 * российской редакции: показать её иностранцу безвредно, обратное — нет.
 *
 * Локацию браузер без запроса разрешений не сообщает, поэтому берём часовой
 * пояс устройства и хост RU-зеркала. VPN часовой пояс не меняет — это плюс.
 *
 * CORE-1559 (решение владельца 03.11): в приложении (в т.ч. из RuStore) контур
 * определяется так же, как на вебе — признак РФ есть и в языках устройства, а
 * не только в выбранном языке интерфейса. Магазин установки контур не задаёт.
 */

// Все часовые пояса IANA, у которых страна — RU (zone1970.tab), плюс устаревшие алиасы.
const RU_TIME_ZONES = new Set([
  'Europe/Kaliningrad', 'Europe/Moscow', 'Europe/Simferopol', 'Europe/Kirov', 'Europe/Volgograd',
  'Europe/Astrakhan', 'Europe/Saratov', 'Europe/Ulyanovsk', 'Europe/Samara',
  'Asia/Yekaterinburg', 'Asia/Omsk', 'Asia/Novosibirsk', 'Asia/Barnaul', 'Asia/Tomsk',
  'Asia/Novokuznetsk', 'Asia/Krasnoyarsk', 'Asia/Irkutsk', 'Asia/Chita', 'Asia/Yakutsk',
  'Asia/Khandyga', 'Asia/Vladivostok', 'Asia/Ust-Nera', 'Asia/Magadan', 'Asia/Sakhalin',
  'Asia/Srednekolymsk', 'Asia/Kamchatka', 'Asia/Anadyr', 'W-SU',
]);

const RU_HOSTS = ['app-ru.agapella.io'];

export type JurisdictionSignals = {
  appLanguage?: string;
  browserLanguages?: readonly string[];
  /** Языки устройства в нативном приложении (expo-localization). */
  deviceLanguages?: readonly string[];
  timeZone?: string;
  host?: string;
};

const isRu = (lang?: string) => !!lang && lang.toLowerCase().startsWith('ru');

export const isForeignJurisdiction = (s: JurisdictionSignals): boolean => {
  if (isRu(s.appLanguage) || (s.browserLanguages ?? []).some(isRu) || (s.deviceLanguages ?? []).some(isRu)) return false;
  if (s.host && RU_HOSTS.includes(s.host.toLowerCase())) return false;
  // Без часового пояса локацию не установить — значит, не «точно не РФ».
  if (!s.timeZone || RU_TIME_ZONES.has(s.timeZone)) return false;
  return true;
};

export const readJurisdictionSignals = (appLanguage?: string): JurisdictionSignals => {
  let timeZone: string | undefined;
  try {
    timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    timeZone = undefined;
  }
  let browserLanguages: readonly string[] | undefined;
  let deviceLanguages: readonly string[] | undefined;
  let host: string | undefined;
  if (Platform.OS === 'web') {
    if (typeof navigator !== 'undefined') {
      browserLanguages = navigator.languages?.length ? navigator.languages : [navigator.language];
      host = typeof location !== 'undefined' ? location.hostname : undefined;
    }
  } else {
    try {
      deviceLanguages = getLocales().map((l) => l.languageTag);
    } catch {
      deviceLanguages = undefined;
    }
  }
  return { appLanguage, browserLanguages, deviceLanguages, timeZone, host };
};
